import { draftsApi } from "../../apis/drafts";

const canonical = (value) => {
  if (Array.isArray(value)) return value.map(canonical);
  if (value && typeof value === "object") return Object.fromEntries(Object.keys(value).sort().map((key) => [key, canonical(value[key])]));
  return value;
};
const equal = (a, b) => JSON.stringify(canonical(a)) === JSON.stringify(canonical(b));
export function newClientId() {
  if (window.crypto?.randomUUID) return window.crypto.randomUUID();
  const bytes = window.crypto.getRandomValues(new Uint8Array(16));
  bytes[6] = (bytes[6] & 15) | 64;
  bytes[8] = (bytes[8] & 63) | 128;
  const hex = Array.from(bytes, (v) => v.toString(16).padStart(2, "0")).join("");
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
}

// One controller per mounted entry. No private payload is stored in browser storage.
export function createDraftController({ draft = null, code, read, api = draftsApi, notify = () => {}, onSaved = () => {}, onCommitted = () => {}, isActive = () => true }) {
  let current = draft;
  const clientId = draft?.client_id || newClientId();
  let pending = null;
  let submitting = false;
  let minimizing = false;
  let committed = false;
  let conflict = false;
  let attempted = Boolean(draft);
  let saved = draft ? { title: draft.title, payload: draft.payload } : null;
  let error = "";
  const emit = () => notify();
  const assertActive = () => {
    if (!isActive()) throw new Error("The form or session has changed. Reopen your saved draft to continue.");
  };
  const saveInternal = async () => {
    assertActive();
    if (committed) throw new Error("Transaction already submitted. This draft cannot be saved again.");
    if (conflict) throw new Error("Draft conflict. Reopen the server draft before saving; your current form has been retained.");
    if (pending) await pending;
    assertActive();
    const next = read();
    if (current && equal(saved, next)) return current;
    const request = async () => {
      attempted = true;
      try {
        const result = current
          ? await api.update(current.id, { ...next, revision: current.revision, schema_version: 1 })
          : await api.create({ ...next, client_id: clientId, form_kind: "transaction", transaction_code: code, schema_version: 1 });
        if (result.submitted_tran_id) {
          current = result;
          committed = true;
          onCommitted(result.id);
          throw new Error("This draft has already been submitted.");
        }
        if (!result.id || !Number.isInteger(result.revision)) throw new Error("Invalid draft response; save was not confirmed.");
        // A repeated client_id may return the original POST, not this request's values.
        if (!equal(result.payload, next.payload) || result.title !== next.title) {
          conflict = true;
          throw new Error("Draft retry returned different saved values. Reopen the server draft; no overwrite was attempted.");
        }
        current = result;
        saved = next;
        error = "";
        onSaved(result);
        return result;
      } catch (e) {
        if (e.status === 409) conflict = true;
        error = e.message;
        throw e;
      }
    };
    pending = request();
    emit();
    try { return await pending; } finally { pending = null; emit(); }
  };
  return {
    assertActive,
    status: () => ({ saving: Boolean(pending), submitting, minimizing, committed, error, draft: current, dirty: !equal(saved, read()) }),
    save: () => {
      if (submitting) return Promise.reject(new Error("Submission in progress. Please wait."));
      if (minimizing) return Promise.reject(new Error("Minimize in progress. Please wait."));
      // Coalesce button presses; submission preparation explicitly saves latest after waiting.
      return pending || saveInternal();
    },
    beginSubmission: () => {
      assertActive();
      if (submitting || minimizing || committed || conflict) throw new Error("Submission unavailable. Wait for the current request or reopen the conflicted draft.");
      submitting = true;
      emit();
    },
    prepareSubmission: async () => {
      if (!submitting) throw new Error("Submission was not locked.");
      if (pending) await pending;
      assertActive();
      if (!current && !attempted) return {};
      const latest = await saveInternal();
      assertActive();
      return { draft_id: latest.id, draft_revision: latest.revision };
    },
    finishSubmission: () => { submitting = false; emit(); },
    beginMinimize: () => {
      assertActive();
      if (submitting || minimizing || committed) throw new Error("Wait for the current request to finish.");
      minimizing = true;
      emit();
    },
    prepareMinimize: async () => {
      if (!minimizing) throw new Error("Minimize was not locked.");
      // A previous explicit save can contain an older snapshot. Wait for it,
      // then save the current values instead of coalescing onto that request.
      if (pending) await pending;
      assertActive();
      const latest = await saveInternal();
      assertActive();
      if (!equal(saved, read())) {
        throw new Error("The entry changed while saving. Review it and minimize again; your form is still open.");
      }
      return latest;
    },
    finishMinimize: () => { minimizing = false; emit(); },
    failSubmission: (e) => {
      error = e.message;
      if (e.status === 409) conflict = true;
      emit();
    },
    commit: () => {
      committed = true;
      submitting = false;
      if (current) onCommitted(current.id);
      emit();
    },
  };
}

export async function saveAndMinimize(controller, navigate, path, onMinimized = () => {}) {
  controller.beginMinimize();
  try {
    const draft = await controller.prepareMinimize();
    controller.assertActive();
    // Also reveal already-saved, unchanged drafts (which skip onSaved).
    // Do this before navigation unmounts the entry, never after a failed save.
    onMinimized(draft);
    navigate(path);
  } finally { controller.finishMinimize(); }
}